import React, {useCallback, useEffect, useState} from "react";
import {Box, Button, MenuItem, Switch, TextField, Tooltip, Typography} from "@mui/material";
import {ScheduleOutlined} from "@mui/icons-material";

import {Blue3, Grey5, Grey6} from "../../theme/styles";
import {ATTACH_FORMATS, AttachFormat, FEED_FORMATS, FeedFormat, FREQUENCIES, Frequency,
        ReportSchedule, deleteSchedule,
        getSchedules, localZone, saveSchedule} from "./scheduleApi";

/**
 * Scheduling a saved report.
 *
 * ONLY FOR A SAVED REPORT, because a schedule references a definition rather than copying one —
 * there is nothing to point at until the report has been kept. The control says so rather than
 * appearing and refusing.
 *
 * IT RUNS FOR YOU, AND THE SCREEN SAYS SO PLAINLY. This is the part most worth being unambiguous
 * about: the run happens under YOUR persona and lands in YOUR history, and nothing is sent to
 * anybody. A reader who assumes otherwise — that scheduling means mailing a team — would be wrong
 * in a way that matters, so the words are on screen rather than in the guide.
 *
 * THE PERSONA IS NOT A FIELD HERE. It travels in the headers this app already sends, is verified
 * when the schedule is saved, and is verified AGAIN against the owner every time it runs. Offering
 * it as a control would suggest you could schedule a scope you do not hold.
 *
 * A STOPPED SCHEDULE SAYS WHY. The server disables one whose owner has gone or lost the persona,
 * and records the reason; a schedule that silently stopped is one somebody thinks is still running.
 */

type Props = {
    /** The saved report, or null while the shelves are still a draft. */
    reportUSID: string | null;
};

export default function ScheduleBar({reportUSID}: Props) {
    const [schedule, setSchedule] = useState<ReportSchedule | null>(null);
    const [open, setOpen] = useState(false);
    const [frequency, setFrequency] = useState<Frequency>("Daily");
    // A LINK UNLESS SOMEBODY CHOOSES OTHERWISE. Defaulting this to "xlsx" would make every schedule
    // saved from here start mailing files, which is the opposite of the trade being deliberate.
    const [attach, setAttach] = useState<AttachFormat>("none");
    const [feed, setFeed] = useState<FeedFormat>("none");
    const [runAt, setRunAt] = useState("02:00");
    const [busy, setBusy] = useState(false);
    const [failure, setFailure] = useState<string | null>(null);

    const load = useCallback(() => {
        if (!reportUSID) { setSchedule(null); return; }
        getSchedules(reportUSID)
            .then(all => {
                const mine = all[0] ?? null;
                setSchedule(mine);
                if (mine) {
                    setFrequency(mine.Frequency);
                    setAttach(mine.AttachFormat ?? "none");
                    setFeed(mine.FeedFormat ?? "none");
                    setRunAt(mine.RunAtTime ?? "02:00");
                }
            })
            .catch(() => setSchedule(null));
    }, [reportUSID]);

    useEffect(() => { load(); setOpen(false); }, [load]);

    if (!reportUSID) return null;

    const keep = (enabled: boolean) => {
        setBusy(true);
        setFailure(null);
        saveSchedule({USID: schedule?.USID, ReportUSID: reportUSID, Frequency: frequency,
                      // AN HOURLY SCHEDULE HAS NO TIME OF DAY. Sending one would be a field the
                      // server ignores, which is a field somebody will later believe in.
                      RunAtTime: frequency === "Hourly" ? null : runAt,
                      Timezone: localZone(), Enabled: enabled, AttachFormat: attach,
                      FeedFormat: feed})
            .then(() => { load(); setOpen(false); })
            .catch((error: any) => setFailure(
                error?.response?.data?.Message ?? "That schedule could not be saved."))
            .finally(() => setBusy(false));
    };

    const stop = () => {
        if (!schedule) return;
        setBusy(true);
        deleteSchedule(schedule.USID)
            .then(() => { setSchedule(null); setOpen(false); })
            .catch((error: any) => setFailure(
                error?.response?.data?.Message ?? "That schedule could not be removed."))
            .finally(() => setBusy(false));
    };

    return (
        // mb, BECAUSE WHAT FOLLOWS IS A HEADING. Closed, this is a single line of blue text sitting
        // straight on top of the FIELDS heading, and the two read as one stack rather than as a
        // control and the section beneath it.
        <Box sx={{mt: 1.5, mb: 2.5}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                <ScheduleOutlined sx={{fontSize: 16, color: schedule?.Enabled ? Blue3 : Grey6}}/>
                <Typography onClick={() => setOpen(one => !one)}
                            role="button"
                            aria-label="Run this report on a schedule"
                            sx={{fontSize: 12.5, color: Blue3, cursor: "pointer",
                                 "&:hover": {textDecoration: "underline"}}}>
                    {schedule?.Enabled
                        ? `Runs ${schedule.Frequency.toLowerCase()}${schedule.RunAtTime ? ` at ${schedule.RunAtTime}` : ""}`
                        : "Run this on a schedule"}
                </Typography>

                {/* THE NEXT RUN, BESIDE THE CADENCE. "Runs daily" is a claim; a date is the thing
                    somebody checks it against. */}
                {schedule?.Enabled && schedule.NextRunDatetime ? (
                    <Typography sx={{fontSize: 11.5, color: Grey6}}>
                        next {String(schedule.NextRunDatetime).slice(0, 16).replace("T", " ")}
                    </Typography>
                ) : null}
            </Box>

            {/* A SCHEDULE THE SERVER STOPPED SAYS WHY, whether or not the panel is open. It is the
                one thing here somebody needs to see without going looking. */}
            {schedule && !schedule.Enabled && schedule.StoppedReason ? (
                <Typography sx={{fontSize: 12.5, color: "#B42318", mt: 0.5}}>
                    Stopped: {schedule.StoppedReason}
                </Typography>
            ) : null}

            {open ? (
                <Box sx={{mt: 1, p: 2, border: "1px solid #E4E7EC", borderRadius: "8px",
                          maxWidth: 760, backgroundColor: "#FAFBFC"}}>
                    <Typography sx={{fontSize: 12.5, color: Grey5, mb: 1.5, maxWidth: "80ch"}}>
                        The report runs on its own and the result is kept in its history, exactly as
                        a run you press yourself is. <b>It runs as you, under your current persona,
                        and it is sent to you and to nobody else.</b> If you stop being able to use
                        that persona, the schedule stops and says so rather than quietly showing
                        less.
                    </Typography>

                    <Box sx={{display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap"}}>
                        <TextField select size="small" label="How often" sx={{width: 150}}
                                   value={frequency}
                                   onChange={event => setFrequency(event.target.value as Frequency)}>
                            {FREQUENCIES.map(one => (
                                <MenuItem key={one} value={one}>{one}</MenuItem>
                            ))}
                        </TextField>

                        {frequency === "Hourly" ? (
                            <Typography sx={{fontSize: 12.5, color: Grey6}}>
                                On the hour, from when you save this.
                            </Typography>
                        ) : (
                            <>
                                <TextField size="small" label="At" sx={{width: 110}} value={runAt}
                                           placeholder="02:00"
                                           onChange={event => setRunAt(event.target.value)}/>
                                {/* THE ZONE IS SHOWN, NOT CHOSEN. It is the browser's own, and a
                                    picker would invite somebody to set a zone they do not live in
                                    and then wonder why the report arrived at the wrong time. */}
                                <Typography sx={{fontSize: 12.5, color: Grey6}}>
                                    {localZone()} — stays at this local time across daylight saving.
                                </Typography>
                            </>
                        )}
                    </Box>

                    {/* THE TRADE IS STATED WHERE THE CHOICE IS MADE, not in a guide nobody opens.
                        A link can be withdrawn and checks who is asking; a file cannot and does
                        not. Somebody choosing to attach should be told that in the moment, once,
                        plainly — and then trusted, because it is their own data going to their own
                        mailbox. */}
                    <Box sx={{display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
                              mt: 2}}>
                        <TextField select size="small" label="Email me" sx={{width: 230}}
                                   value={attach}
                                   onChange={event =>
                                       setAttach(event.target.value as AttachFormat)}>
                            {ATTACH_FORMATS.map(one => (
                                <MenuItem key={one.id} value={one.id}>{one.label}</MenuItem>
                            ))}
                        </TextField>

                        <Typography sx={{fontSize: 12.5, color: Grey6, maxWidth: "62ch"}}>
                            {attach === "none"
                                ? "The email says how many rows and links to the result, which "
                                  + "opens under your persona and can be withdrawn."
                                : "The file leaves the platform. It can be forwarded, it does not "
                                  + "expire, and it cannot be recalled — and unlike the link it is "
                                  + "a copy, so it will not change when the report does."}
                        </Typography>
                    </Box>

                    {/* THE OTHER DESTINATION, AND A DIFFERENT PROMISE FROM THE ONE ABOVE. That
                        one sends a person a file; this one puts a file where something else reads
                        it on a cadence. Kept as its own row rather than folded into "Email me",
                        because they are not alternatives -- a schedule may do both. */}
                    <Box sx={{display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
                              mt: 2}}>
                        <TextField select size="small" label="Deliver to" sx={{width: 230}}
                                   value={feed}
                                   onChange={event => setFeed(event.target.value as FeedFormat)}>
                            {FEED_FORMATS.map(one => (
                                <MenuItem key={one.id} value={one.id}>{one.label}</MenuItem>
                            ))}
                        </TextField>

                        <Typography sx={{fontSize: 12.5, color: Grey6, maxWidth: "62ch"}}>
                            {feed === "none"
                                ? "Nothing is written anywhere but the run history."
                                : "Each run writes one file, kept, for a system that reads the "
                                  + "folder on a schedule. A run that hits the row cap is NOT "
                                  + "delivered — a partial file looks complete to whatever loads "
                                  + "it — and the schedule stops rather than delivering part of "
                                  + "an answer."}
                        </Typography>
                    </Box>

                    {/* WHERE THE LAST ONE LANDED, once there has been one. A feed nobody can see
                        working is a feed somebody checks by opening a bucket. */}
                    {schedule?.LastFeedKey ? (
                        <Typography sx={{fontSize: 11.5, color: Grey6, mt: 1}}>
                            Last delivered {String(schedule.LastFeedDatetime ?? "").slice(0, 16).replace("T", " ")}
                            {schedule.LastFeedRows != null ? ` — ${schedule.LastFeedRows} rows` : ""}
                        </Typography>
                    ) : null}

                    {failure ? (
                        <Typography sx={{fontSize: 12.5, color: "#B42318", mt: 1.5}}>{failure}</Typography>
                    ) : null}

                    <Box sx={{display: "flex", alignItems: "center", gap: 1.5, mt: 2}}>
                        {/* SAVE KEEPS IT AS IT IS; ONLY THE SWITCH STARTS AND STOPS IT.
                            This said keep(true), so changing any setting on a PAUSED schedule
                            silently resumed it -- somebody adjusting the time of a schedule they
                            had deliberately stopped would set it running again, and the only sign
                            would be the word under the switch changing. Caught in prod by doing
                            exactly that: saving an attachment preference resumed a schedule that
                            had been paused on purpose.

                            THE BUTTON DOES DOUBLE DUTY, which is where the bug came from: with no
                            schedule it reads "Start the schedule" and true is right; with one it
                            reads "Save changes" and true is a second, unasked-for instruction. */}
                        <Button size="small" variant="outlined" disabled={busy}
                                onClick={() => keep(schedule ? !!schedule.Enabled : true)}
                                sx={{textTransform: "none", fontSize: 13, borderColor: "#A8A9AF"}}>
                            {busy ? "Saving…" : schedule ? "Save changes" : "Start the schedule"}
                        </Button>

                        {schedule ? (
                            <>
                                <Tooltip title={schedule.Enabled
                                    ? "Stop running it, and keep the settings"
                                    : "Start it again"}>
                                    {/* slotProps, NOT inputProps: MUI v9 moved it, and the old
                                        name is a type error rather than a silent no-op only
                                        because this file is TypeScript. Same move as PaperProps
                                        on Drawer. */}
                                    <Switch size="small" checked={schedule.Enabled} disabled={busy}
                                            slotProps={{input: {"aria-label": "Schedule enabled"}}}
                                            onChange={event => keep(event.target.checked)}/>
                                </Tooltip>
                                <Typography sx={{fontSize: 12.5, color: Grey6}}>
                                    {schedule.Enabled ? "Running" : "Paused"}
                                </Typography>
                                <div style={{flexGrow: 1}}/>
                                <Typography onClick={stop}
                                            role="button"
                                            aria-label="Remove this schedule"
                                            sx={{fontSize: 12.5, color: "#B42318", cursor: "pointer"}}>
                                    Remove
                                </Typography>
                            </>
                        ) : null}
                    </Box>

                    {schedule?.LastRunDatetime ? (
                        <Typography sx={{fontSize: 11.5, color: Grey6, mt: 1.5}}>
                            Last ran {String(schedule.LastRunDatetime).slice(0, 16).replace("T", " ")}
                            {" — the result is in this report's history."}
                        </Typography>
                    ) : null}
                </Box>
            ) : null}
        </Box>
    );
}
